/**
 * Listens for TWOPOINT_COMMAND postMessages from parent and dispatches to
 * iframe-side primitive handlers.
 *
 * Security:
 * - Validates event.origin against parentOrigin captured during handshake.
 *   No fallback to '*'.
 * - Whitelisted action names only — unknown actions logged and dropped.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWidgetEvent } from '../contexts/WidgetEventContext';
import { COMMAND_MESSAGE_TYPE } from '../config/deepLink';
import { executeIframeCommand } from '../widget/command-handlers-iframe';
import { getParentOrigin } from '../utils/iframeMessenger';
import type { IframeCommand } from '../widget/types';

const KNOWN_ACTIONS: ReadonlySet<IframeCommand['action']> = new Set<IframeCommand['action']>([
  'property.open',
  'lendi.expand',
]);

interface IncomingCommand {
  type: typeof COMMAND_MESSAGE_TYPE;
  action: string;
  payload: unknown;
}

const isCommandMessage = (data: unknown): data is IncomingCommand =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === COMMAND_MESSAGE_TYPE &&
  typeof (data as { action?: unknown }).action === 'string';

const originOf = (raw: string): string | null => {
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
};

export const useWidgetCommandBus = (): void => {
  const navigate = useNavigate();
  const { setLendiPending } = useWidgetEvent();

  useEffect(() => {
    const handle = (event: MessageEvent): void => {
      const parentOrigin = getParentOrigin();
      if (!parentOrigin) return;

      const expectedOrigin = originOf(parentOrigin);
      if (!expectedOrigin || event.origin !== expectedOrigin) return;

      if (!isCommandMessage(event.data)) return;

      const { action, payload } = event.data;
      if (!KNOWN_ACTIONS.has(action as IframeCommand['action'])) {
        console.warn('[TwoPoint iframe] Unknown command action:', action);
        return;
      }

      executeIframeCommand(
        { kind: 'iframe', action, payload } as IframeCommand,
        { navigate, setLendiPending }
      );
    };

    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [navigate, setLendiPending]);
};
